import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, switchMap, startWith, map } from 'rxjs/operators';
import { of } from 'rxjs';
import { ClickActions } from './click.actions';
import { ClickCommandsService } from '../../api-services/click/click_commands.service';
import { ClickTemplatesService } from '../../api-services/click/click_templates.service';
import { AccountClickService } from '../../api-services/account/account_click.service';
import { AppActions } from '../app/app.actions';

@Injectable()
export class ClickEffects {
  private actions$ = inject(Actions)

  constructor(
    private AccountClickService: AccountClickService,
    private commandsService: ClickCommandsService,
    private templatesService: ClickTemplatesService
  ) { }

  searchDevice$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.searchDevice),
      concatMap((action) =>
        this.AccountClickService.searchDevice(action.email).pipe(
          switchMap(data => [
            ClickActions.searchDeviceSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadCommands$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.loadCommands),
      concatMap(() =>
        this.commandsService.getCommands().pipe(
          switchMap(data => [
            ClickActions.loadCommandsSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  CreateCommand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.createCommand),
      concatMap((action) =>
        this.commandsService.createCommand(action.payload).pipe(
          switchMap(data => [
            ClickActions.createCommandSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            ClickActions.loadCommands()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  UpdateCommand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.updateCommand),
      concatMap((action) =>
        this.commandsService.updateCommand(action.payload).pipe(
          switchMap(data => [
            ClickActions.updateCommandSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            ClickActions.loadCommands()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadCommandById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.loadCommandById),
      concatMap((action) =>
        this.commandsService.getCommandById(action.id).pipe(
          switchMap(data => [
            ClickActions.loadCommandByIdSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  deleteCommand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.deleteCommand),
      concatMap((action) =>
        this.commandsService.deleteCommand(action.id).pipe(
          switchMap(() => [
            ClickActions.loadCommands(),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadTemplates$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.loadTemplates),
      concatMap(() =>
        this.templatesService.getTemplates().pipe(
          switchMap(data => [
            ClickActions.loadTemplatesSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  CreateTemplate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.createTemplate),
      concatMap((action) =>
        this.templatesService.createTemplate(action.payload).pipe(
          switchMap(data => [
            ClickActions.createTemplateSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            ClickActions.loadTemplates()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  UpdateTemplate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.updateTemplate),
      concatMap((action) =>
        this.templatesService.updateTemplate(action.payload).pipe(
          switchMap(data => [
            ClickActions.updateTemplateSuccess({ data }),
            AppActions.setLoading({ loading: false }),
            ClickActions.loadTemplates()
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  loadTemplateById$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.loadTemplateById),
      concatMap((action) =>
        this.templatesService.getTemplateById(action.id).pipe(
          switchMap(data => [
            ClickActions.loadTemplateByIdSuccess({ data }),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  deleteTemplate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.deleteTemplate),
      concatMap((action) =>
        this.templatesService.deleteTemplate(action.id).pipe(
          switchMap(() => [
            ClickActions.loadTemplates(),
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  AddNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.addNotification),
      concatMap((action) =>
        this.templatesService.addNotificationToTemplate(action.templateId, action.eventDefinitionId).pipe(
          switchMap(data => [
            ClickActions.addNotificationSuccess({ data }),
            ClickActions.loadTemplateById({ id: action.templateId }), // Recargar template
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  RemoveNotification$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.removeNotification),
      concatMap((action) =>
        this.templatesService.removeNotificationFromTemplate(action.templateId, action.eventDefinitionId).pipe(
          switchMap(data => [
            ClickActions.removeNotificationSuccess({ data }),
            ClickActions.loadTemplateById({ id: action.templateId }), // Recargar template
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  AddCommand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.addCommand),
      concatMap((action) =>
        this.templatesService.addCommandToTemplate(action.templateId, action.commandId).pipe(
          switchMap(data => [
            ClickActions.addCommandSuccess({ data }),
            ClickActions.loadTemplateById({ id: action.templateId }), // Recargar template
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });

  RemoveCommand$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClickActions.removeCommand),
      concatMap((action) =>
        this.templatesService.removeCommandFromTemplate(action.templateId, action.commandId).pipe(
          switchMap(data => [
            ClickActions.removeCommandSuccess({ data }),
            ClickActions.loadTemplateById({ id: action.templateId }), // Recargar template
            AppActions.setLoading({ loading: false })
          ]),
          catchError(error => of(AppActions.setError({ error }))),
          startWith(AppActions.setLoading({ loading: true })),
        )
      )
    );
  });


}
